// ABOUTME: Quick To-do widget. Signed-in users sync through Firestore; guests use localStorage.
// ABOUTME: State lives in useQuickTodo; this file is presentation only.
import React, { useState } from "react";
import { useAuthStore } from "../stores/auth";
import { useQuickTodo } from "../hooks/useQuickTodo";

const QuickTodo: React.FC = () => {
  // Read the store directly rather than useAuth(): this widget is created by
  // the registry with no props, and the store is the cheapest subscription.
  const userId = useAuthStore((s) => s.user?.uid);
  const { todos, update, saveError, loadError } = useQuickTodo(userId);
  const [input, setInput] = useState("");

  const addTodo = () => {
    if (input.trim()) {
      update((prev) => [...prev, { id: `${Date.now()}`, text: input.trim(), done: false }]);
      setInput("");
    }
  };

  const toggleTodo = (id: string) => {
    update((prev) => prev.map((todo) => (todo.id === id ? { ...todo, done: !todo.done } : todo)));
  };

  const removeTodo = (id: string) => {
    update((prev) => prev.filter((todo) => todo.id !== id));
  };

  const clearCompleted = () => update((prev) => prev.filter((t) => !t.done));

  const doneCount = todos.filter((t) => t.done).length;
  const problem = saveError ?? loadError;

  return (
    <div className="rounded-[var(--radius-md)] bg-[var(--color-bg-secondary)] border border-[var(--color-border)] flex flex-col transition-colors relative h-full">
      {/* Input row */}
      <div className="flex items-center gap-1.5 px-3 pt-3 pb-1.5">
        <input
          className="flex-1 min-w-0 rounded-md border border-[var(--color-border)] px-2.5 py-1.5 text-sm bg-[var(--color-bg)] text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] focus:outline-none focus:ring-2 focus:ring-[color-mix(in_srgb,var(--color-accent)_40%,transparent)] focus:border-[var(--color-accent)] transition-all"
          placeholder="Add a task…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") addTodo();
          }}
        />
        <button
          className="tap-target w-7 h-7 flex items-center justify-center rounded-md text-[var(--color-text-muted)] hover:text-[var(--color-accent)] hover:bg-[var(--color-bg-hover)] transition-colors shrink-0"
          onClick={addTodo}
          aria-label="Add task"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
        </button>
      </div>

      {problem && (
        <div role="alert" className="mx-3 mb-1 text-xs text-[var(--color-danger)]">
          {saveError ? "Couldn't save your tasks" : "Couldn't load your tasks"}: {problem.message}
        </div>
      )}

      {/* Todo list */}
      <ul className="flex flex-col px-1.5 pb-1.5 overflow-y-auto flex-1">
        {todos.length === 0 && (
          <li className="text-sm text-[var(--color-text-muted)] px-2 pt-2 pb-1">No tasks yet</li>
        )}
        {todos.map((todo) => (
          <li
            key={todo.id}
            className="flex items-center gap-2.5 group rounded-md px-2 py-1 [@media(pointer:coarse)]:min-h-[44px] hover:bg-[var(--color-bg-hover)] transition-colors"
          >
            {/* Custom checkbox */}
            <button
              role="checkbox"
              aria-checked={todo.done}
              aria-label={todo.text}
              onClick={() => toggleTodo(todo.id)}
              className={`tap-target w-[18px] h-[18px] rounded border-2 shrink-0 flex items-center justify-center transition-all ${
                todo.done
                  ? "bg-[var(--color-accent)] border-[var(--color-accent)]"
                  : "border-[var(--color-text-muted)] hover:border-[var(--color-accent)]"
              }`}
            >
              {todo.done && (
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="white"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </button>
            <span
              className={`flex-1 text-sm leading-snug transition-colors ${
                todo.done
                  ? "line-through text-[var(--color-text-muted)]"
                  : "text-[var(--color-text-primary)]"
              }`}
            >
              {todo.text}
            </span>
            <button
              className="tap-target [@media(hover:hover)]:opacity-0 group-hover:opacity-100 text-[var(--color-text-muted)] hover:text-[var(--color-danger)] transition-opacity p-0.5"
              onClick={() => removeTodo(todo.id)}
              aria-label={`Remove ${todo.text}`}
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </li>
        ))}
      </ul>

      {/* Footer */}
      {todos.length > 0 && (
        <div className="border-t border-[var(--color-border)] px-3 py-1.5 flex justify-between items-center">
          <span className="text-xs text-[var(--color-text-muted)]">
            {doneCount}/{todos.length} done
          </span>
          {doneCount > 0 && (
            <button
              className="text-xs text-[var(--color-text-muted)] hover:text-[var(--color-danger)] transition-colors"
              onClick={clearCompleted}
            >
              Clear done
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default QuickTodo;
